import { useEffect, useMemo, useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import CardSelection from "@components/ui/selection/CardSelection";
import DatePicker from "@components/ui/selection/DatePicker";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import { Planning, PlanningModel, PlanningSection } from "types/planning";
import { PlanningTemplate } from "types/planning_template";
import { PartnerInfos, PlanningTemplateSection, Project } from "types";
import { getTemplateSections, headerBg, headerText } from "../../utils/planning_structure";
import { mapTemplateWeeksToProject } from "../../utils/planning_utils";
import { planningsApi } from "@services/api/plannings";
import { projectsApi } from "@services/api/projects";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { syncDecennalesFolder } from "@helpers/decennalesHelpers";

/** Snap a date to the most recent Monday (or same day if already Monday) */
function toMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toIsoDate(date: Date): string {
  // Use local date parts to avoid UTC timezone shift (toISOString() converts to UTC)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function secondsToIsoDate(seconds: number): string {
  return toIsoDate(toMonday(new Date(seconds * 1000)));
}

function buildInitialSections(
  partners: PartnerInfos[],
  template: PlanningTemplate | null,
  startDateSeconds: number,
): PlanningSection[] {
  if (!template) return [];
  const templateSections = template.sections.filter((s) => !s.deleted_at);
  const projectStart = toMonday(new Date(startDateSeconds * 1000));

  return templateSections.map((sectionDef) => {
    // Find candidates whose company_types includes this section key
    // Sort by number of types ascending (specialists first)
    const candidates = partners
      .filter((p) => p.company_types?.includes(sectionDef.section_key))
      .sort((a, b) => (a.company_types?.length || 0) - (b.company_types?.length || 0));

    const artisanIds = candidates.map((p) => p.id);

    return {
      section_key: sectionDef.section_key,
      // Snapshot the template's label so future template edits don't
      // retroactively change this project's planning.
      label: sectionDef.label,
      artisan_ids: artisanIds,
      steps: (sectionDef.steps || [])
        .filter((st) => !st.deleted_at)
        .map((step) => {
          // Map default weeks through working-slot space (holidays shift)
          const mapped = step.default_weeks?.length
            ? mapTemplateWeeksToProject(
                step.default_weeks,
                template.model,
                projectStart,
              )
            : [];
          // Store under _template (always preserved) + each assigned artisan
          const colored_weeks: Record<string, number[]> = {};
          if (mapped.length > 0) {
            colored_weeks._template = mapped;
            for (const artisanId of artisanIds) {
              colored_weeks[artisanId] = [...mapped];
            }
          }
          return {
            step_key: step.step_key,
            label: step.label,
            colored_weeks,
          };
        }),
    };
  });
}

const MODEL_WEEKS: Record<PlanningModel, number> = {
  "11_mois": 48,
  "12_mois": 52,
  "12_mois_ordre": 52,
  "13_mois_avec_conges": 56,
  "14_mois_avec_conges": 60,
  "15_mois_avec_conges": 64,
};



function getPlanningDateRange(startDateIso: string, model: PlanningModel): { startIso: string; endIso: string } {
  const start = new Date(startDateIso);
  const weeks = MODEL_WEEKS[model];
  const end = new Date(start.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  return { startIso: toIsoDate(start), endIso: toIsoDate(end) };
}


const modelOptions = [
  {
    value: "11_mois",
    title: "11 mois avec congés",
    description: "48 semaines — de Janvier à Novembre",
  },
  {
    value: "12_mois",
    title: "12 mois avec congés",
    description: "52 semaines — d'Octobre à Septembre (année suivante)",
  },
  {
    value: "12_mois_ordre",
    title: "12 mois ordre strict",
    description: "52 semaines — d'Octobre à Septembre (sans congés)",
  },
  {
    value: "13_mois_avec_conges",
    title: "13 mois avec congés",
    description: "56 semaines — de Janvier à Janvier (année suivante)",
  },
  {
    value: "14_mois_avec_conges",
    title: "14 mois avec congés",
    description: "60 semaines — de Janvier à Février (année +1)",
  },
  {
    value: "15_mois_avec_conges",
    title: "15 mois avec congés",
    description: "64 semaines — de Janvier à Mars (année +1)",
  },
];

interface Props {
  onClose: () => void;
  onCreated?: (planning: Planning) => void;
  onUpdated?: (planning: Planning) => void;
  onProjectUpdated?: (project: Project) => void;
  projectId: string;
  orgId: string;
  project: Project;
  partners: PartnerInfos[];
  existingPlanning?: Planning;
}

type Step = "model" | "sections";

export const PlanningSetupModal = ({
  onClose,
  onCreated,
  onUpdated,
  onProjectUpdated,
  projectId,
  orgId,
  project,
  partners,
  existingPlanning,
}: Props) => {
  const isEditing = !!existingPlanning;

  const [step, setStep] = useState<Step>("model");
  const [model, setModel] = useState<PlanningModel>(
    existingPlanning?.model ?? "11_mois"
  );
  const [startDateSeconds, setStartDateSeconds] = useState<number>(
    existingPlanning
      ? Math.floor(new Date(existingPlanning.start_date).getTime() / 1000)
      : Math.floor(toMonday(new Date()).getTime() / 1000)
  );
  const [saving, setSaving] = useState(false);
  const planningTemplates = useProjectStore((state) => state.planningTemplates);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    existingPlanning?.planning_template_id || "",
  );

  // Template-derived sections for display + initial planning build
  const selectedTemplate = useMemo(
    () => planningTemplates.find((t) => t.id === selectedTemplateId),
    [planningTemplates, selectedTemplateId],
  );
  const templateSections = useMemo(
    () => getTemplateSections(selectedTemplate),
    [selectedTemplate],
  );

  const [sections, setSections] = useState<PlanningSection[]>(
    existingPlanning?.sections ?? [],
  );

  // When the selected template changes (and there's no existing planning),
  // re-seed the sections from the new template's structure — including the
  // template's default colored weeks mapped to the project's start date.
  useEffect(() => {
    if (existingPlanning) return;
    if (selectedTemplate) {
      // Adopt the template's model to keep week counts consistent
      setModel(selectedTemplate.model);
    }
    setSections(buildInitialSections(partners, selectedTemplate || null, startDateSeconds));
    // Intentionally NOT depending on startDateSeconds here: handleStartDateChange
    // takes care of re-mapping when only the start date changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPlanning, partners, selectedTemplate]);

  /** When start date changes, re-map from template if one is selected, otherwise shift */
  function handleStartDateChange(newSeconds: number) {
    setStartDateSeconds(newSeconds);

    const tpl = getSelectedTemplate();
    if (tpl) {
      // Re-apply template with new start date (holidays shift)
      const projectStart = toMonday(new Date(newSeconds * 1000));
      setSections((prev) =>
        prev.map((section) => {
          const tplSection = tpl.sections.find(
            (ts) => ts.section_key === section.section_key,
          );
          if (!tplSection) return section;
          return {
            ...section,
            steps: section.steps.map((step) => {
              const tplStep = tplSection.steps.find(
                (ts) => ts.step_key === step.step_key,
              );
              if (!tplStep || tplStep.default_weeks.length === 0) return step;
              const weeks = mapTemplateWeeksToProject(
                tplStep.default_weeks,
                tpl.model,
                projectStart,
              );
              if (weeks.length === 0) return step;
              const colored_weeks: Record<string, number[]> = {
                _template: weeks,
              };
              for (const artisanId of section.artisan_ids) {
                colored_weeks[artisanId] = [...weeks];
              }
              return { ...step, colored_weeks };
            }),
          };
        }),
      );
      return;
    }

    // No template — simple shift to keep cells on same calendar weeks
    const oldMonday = toMonday(new Date(startDateSeconds * 1000));
    const newMonday = toMonday(new Date(newSeconds * 1000));
    const deltaWeeks = Math.round(
      (newMonday.getTime() - oldMonday.getTime()) / (7 * 86400000),
    );
    if (deltaWeeks === 0) return;

    const totalWeeks = MODEL_WEEKS[model];
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        steps: section.steps.map((step) => {
          const shifted: Record<string, number[]> = {};
          for (const [artisanId, weeks] of Object.entries(step.colored_weeks)) {
            shifted[artisanId] = weeks
              .map((w) => w - deltaWeeks)
              .filter((w) => w >= 0 && w < totalWeeks);
          }
          return { ...step, colored_weeks: shifted };
        }),
      })),
    );
  }

  function getSelectedTemplate() {
    return planningTemplates.find((t) => t.id === selectedTemplateId);
  }

  function toggleArtisan(sectionKey: string, artisanId: string) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        const has = s.artisan_ids.includes(artisanId);
        if (has) {
          // Remove artisan
          return {
            ...s,
            artisan_ids: s.artisan_ids.filter((id) => id !== artisanId),
            steps: s.steps.map((step) => {
              const { [artisanId]: _, ...rest } = step.colored_weeks;
              return { ...step, colored_weeks: rest };
            }),
          };
        }
        // Add artisan — pre-fill from _template weeks if available
        return {
          ...s,
          artisan_ids: [...s.artisan_ids, artisanId],
          steps: s.steps.map((step) => {
            const templateWeeks = step.colored_weeks["_template"] || [];
            return {
              ...step,
              colored_weeks: {
                ...step.colored_weeks,
                ...(templateWeeks.length > 0
                  ? { [artisanId]: [...templateWeeks] }
                  : {}),
              },
            };
          }),
        };
      })
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (isEditing && existingPlanning) {
        const newStartIso = secondsToIsoDate(startDateSeconds);
        const planning = await planningsApi.update(existingPlanning.id, {
          model,
          start_date: newStartIso,
          sections,
          planning_template_id: selectedTemplateId || undefined,
        });

        // Planning end date may have shifted — re-verify décennale coverage
        // and swap in better-matching ones when possible.
        try {
          const planningRange = getPlanningDateRange(newStartIso, model);
          const orgPartners = useProjectStore.getState().partners;
          const { folders: updatedFolders, warnings } = syncDecennalesFolder(
            project,
            orgPartners,
            planningRange.endIso,
          );
          warnings.forEach((w) =>
            showNotification({ type: "error", message: w }),
          );
          const updatedProject: Project = { ...project, folders: updatedFolders };
          await projectsApi.update(projectId, { folders: updatedFolders as any });
          onProjectUpdated?.(updatedProject);
        } catch (e) {
          console.error("Failed to resync décennales after planning update:", e);
        }

        showNotification({ type: "success", message: "Planning modifié avec succès" });
        onUpdated?.(planning);
      } else {
        const startIso = secondsToIsoDate(startDateSeconds);
        const planning = await planningsApi.create({
          project_id: projectId,
          org_id: orgId,
          model,
          start_date: startIso,
          sections,
          planning_template_id: selectedTemplateId || undefined,
        });

        // Re-sync décennales against the new planning's end date. Partners
        // already have a décennale attached at project/add time; this pass
        // swaps it for one covering the planning end when possible, and
        // flags any partner without coverage.
        const planningRange = getPlanningDateRange(startIso, model);
        const orgPartners = useProjectStore.getState().partners;
        const { folders: updatedFolders, warnings } = syncDecennalesFolder(
          project,
          orgPartners,
          planningRange.endIso,
        );

        warnings.forEach((w) =>
          showNotification({ type: "error", message: w }),
        );

        const updatedProject: Project = { ...project, folders: updatedFolders };
        await projectsApi.update(projectId, { folders: updatedFolders as any });
        onProjectUpdated?.(updatedProject);

        showNotification({ type: "success", message: "Planning créé avec succès" });
        onCreated?.(planning);
      }
      onClose();
    } catch {
      showNotification({
        type: "error",
        message: isEditing
          ? "Erreur lors de la modification du planning"
          : "Erreur lors de la création du planning",
      });
    } finally {
      setSaving(false);
    }
  }

  const isoDate = secondsToIsoDate(startDateSeconds);

  return (
    <BaseModal
      onClose={onClose}
      title={isEditing ? "Modifier le planning" : "Créer un planning prévisionnel"}
      startIcon={<Icon1 name="planning" size={20} />}
      width="max-w-2xl"
    >
      <div className="flex flex-col gap-6 p-6">
        {step === "model" && (
          <>
            {planningTemplates.length > 0 ? (
              <DropDown
                label="Modèle de planning"
                placeholder="Sélectionner un modèle"
                options={[...planningTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
                value={selectedTemplateId}
                onSelect={(val) => {
                  // Setting the ID is enough — the effect on selectedTemplate
                  // re-seeds the sections (and applies the template's default
                  // weeks) automatically.
                  setSelectedTemplateId(val as string);
                }}
                category="Modèle"
              />
            ) : (
              <CardSelection
                label="Durée du chantier"
                options={modelOptions}
                value={model}
                onChange={(v) => setModel(v as PlanningModel)}
              />
            )}

            <DatePicker
              label="Date de démarrage (semaine)"
              seconds={startDateSeconds}
              onChange={(s) => handleStartDateChange(s)}
            />

            {startDateSeconds > 0 && (
              <div className="text-h3 text-neutral-500 bg-neutral-100 rounded-xl px-4 py-2">
                Démarrage le lundi <strong>{isoDate}</strong>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                label="Suivant — Artisans"
                onClick={() => setStep("sections")}
              />
            </div>
          </>
        )}

        {step === "sections" && (
          <>
            <div className="text-h2 font-medium text-neutral-700">
              Assigner les artisans par corps de métier
            </div>
            <div className="text-h3 text-neutral-400">
              Sélectionnez les intervenants du projet pour chaque section du planning.
            </div>

            <div className="flex flex-col gap-4 max-h-[50vh] overflow-y-auto">
              {templateSections.length === 0 && (
                <div className="text-h3 text-neutral-400 py-4 text-center">
                  Sélectionnez un modèle de planning pour voir les sections.
                </div>
              )}
              {templateSections.map((sectionDef) => {
                const sectionState = sections.find(
                  (s) => s.section_key === sectionDef.section_key
                );
                const selectedIds = sectionState?.artisan_ids || [];

                return (
                  <div key={sectionDef.section_key} className="flex flex-col gap-2">
                    <div
                      className="px-3 py-1 rounded-lg text-h3 font-bold"
                      style={{
                        backgroundColor: headerBg(sectionDef),
                        color: headerText(sectionDef),
                      }}
                    >
                      {sectionDef.label || sectionDef.section_key}
                    </div>
                    {partners.length === 0 ? (
                      <div className="text-h4 text-neutral-400 pl-2">
                        Aucun intervenant sur ce projet
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 pl-2">
                        {partners.map((partner) => {
                          const isSelected = selectedIds.includes(partner.id);
                          return (
                            <button
                              key={partner.id}
                              onClick={() =>
                                toggleArtisan(sectionDef.section_key, partner.id)
                              }
                              className={`px-3 py-1 rounded-full text-h3 border transition-all ${
                                isSelected
                                  ? "bg-neutral-900 text-white border-neutral-900"
                                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                              }`}
                            >
                              {partner.company_name || partner.display_name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between">
              <button
                className="text-h3 text-neutral-400 hover:text-neutral-600"
                onClick={() => setStep("model")}
              >
                ← Retour
              </button>
              <Button
                label={saving ? "Enregistrement..." : isEditing ? "Modifier le planning" : "Créer le planning"}
                onClick={handleSave}
              />
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};
